package music.jammers.client.shared.profile.presentation.viewmodel

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.auth.domain.usecase.LogoutUseCase
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.profile.domain.SaveNewUserProfileDataCase
import music.jammers.client.shared.profile.domain.ValidateProfileUseCase
import music.jammers.client.shared.profile.presentation.ui.ProfileFieldValidator
import music.jammers.shared.user.domain.model.UserProfileUpdateData
import music.limasoul.client.shared.navigation.Navigation
import music.limasoul.client.shared.navigation.Router
import music.limasoul.client.shared.navigation.createTelegramUrl
import music.limasoul.client.shared.user.domain.usecase.GetCurrentUserUseCase
import music.limasoul.client.shared.user.domain.usecase.GetUserUseCase

class ProfileViewModel(
    private val router: Router,
    private val eventAnalytics: EventAnalytics,
    private val getUserUseCase: GetUserUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val saveNewUserProfileDataCase: SaveNewUserProfileDataCase,
    private val profileFieldValidator: ProfileFieldValidator,
    private val validateProfileUseCase: ValidateProfileUseCase,
    private val logoutUseCase: LogoutUseCase,
) : BaseViewModel() {

    internal val state: State<ProfilePageState> get() = _state
    internal val validationState: State<FieldValidationState> get() = _validationState
    internal val editModeState: State<Boolean> get() = _editModeState

    private val _state: MutableState<ProfilePageState> = mutableStateOf(ProfilePageState())
    private val _validationState: MutableState<FieldValidationState> = mutableStateOf(FieldValidationState())
    private val _editModeState: MutableState<Boolean> = mutableStateOf(false)

    internal fun onBind(userId: Long?) {
        loadUserProfile(userId)
    }

    private fun loadUserProfile(userId: Long?) {
        launch(
            customExceptionHandler(router) { _, throwable ->
                eventAnalytics.onError(throwable)

                _state.update {
                    copy(
                        pageDataLoading = false,
                        error = throwable,
                    )
                }
            }
        ) {
            _state.update {
                copy(
                    pageDataLoading = true,
                    userId = userId,
                )
            }

            val user = if (userId == null) {
                getCurrentUserUseCase()
            } else {
                getUserUseCase(userId)
            }

            _state.update {
                copy(
                    pageDataLoading = false,
                    userName = user.name,
                    telegram = user.telegram,
                    picture = user.pictureUrl,
                    city = user.city,
                    needShowAdminSettingsButton = user.isAdmin,
                    editAvailable = userId == null,
                    telegramOpenVisible = userId != null,
                )
            }
        }
    }

    fun onLogOutClicked() {
        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        error = throwable
                    )
                }
            }
        ) {
            logoutUseCase()
            navigateToLogin()
        }
    }

    private fun navigateToLogin() {
        router.navigateTo(Navigation.Login)
    }

    fun onAdminSettingsClicked() {
        navigateToAdminSettings()
    }

    private fun navigateToAdminSettings() {
        router.navigateTo(Navigation.Admin)
    }

    fun onSaveProfileClicked() {
        val updateData = UserProfileUpdateData(
            userName = _state.value.userName,
            city = _state.value.city,
            telegram = _state.value.telegram
        )

        val errors = validateProfileUseCase.invoke(updateData)

        if (errors.isEmpty()) {
            updateProfileData(updateData)
        } else {
            _validationState.update {
                copy(
                    validationErrorMap = errors.associateBy { item ->
                        item.fieldName
                    }
                )
            }
        }
    }

    fun onEditModeClicked() {
        _editModeState.value = true
    }

    fun onEditModeClosed() {
        _editModeState.value = false
        loadUserProfile(_state.value.userId)
    }

    fun onCloseButtonClicked() {
        router.back()
    }

    private fun updateProfileData(updateData: UserProfileUpdateData) {
        _state.update {
            copy(
                profileSavingInProgress = true
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                eventAnalytics.onError(throwable)

                _state.update {
                    copy(
                        profileSavingInProgress = false,
                        error = throwable
                    )
                }
            }
        ) {
            saveNewUserProfileDataCase(updateData)

            _state.update {
                copy(
                    profileSavingInProgress = false
                )
            }

            _editModeState.value = false
        }
    }

    fun onNameChanged(newName: String) {
        _state.update {
            copy(
                userName = newName
            )
        }

        validateField(ValidateProfileUseCase.NAME, newName)
    }

    fun onTelegramChanged(newTelegram: String) {
        _state.update {
            copy(
                telegram = newTelegram
            )
        }

        validateField(ValidateProfileUseCase.TELEGRAM, newTelegram)
    }

    fun onCityChanged(newCity: String) {
        _state.update {
            copy(
                city = newCity
            )
        }

        validateField(ValidateProfileUseCase.CITY, newCity)
    }

    fun onErrorClosed() {
        _state.update {
            copy(
                error = null
            )
        }
    }

    private fun validateField(name: String, value: String) {
        _validationState.update {
            val error = profileFieldValidator.validate(name, value)
            if (error == null) {
                resetError(name)
            } else {
                addError(error)
            }
        }
    }

    fun onTelegramOpen() {
        _state.update {
            copy(
                openUrl = createTelegramUrl(state.value.telegram)
            )
        }
    }

    fun onUrlOpened() {
        _state.update {
            copy(
                openUrl = null,
            )
        }
    }
}